import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixSocialGroupsApiV2GroupMembersServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api_group-members-proxy',
        destPath: '',
      },
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/group-members-proxy',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'groups.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
      {
        srcPath: '/social-groups-proxy/api/members',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/members',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/social-groups-proxy/members',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_groups_members';

/**
 * Add site members to a specific group. Public members are added right away, while private members receive an invitation to join the group.
 *
 * For `SECRET` groups, group admins and group members can add additional members to their group.
 * For `PUBLIC` and `PRIVATE` groups, only group admins can add additional members to their group.
 * They can also allow all group members to add a new member to the group with `group.settings`.
 */
export function addGroupMembers(payload: object): RequestOptionsFactory<any> {
  function __addGroupMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_member',
      method: 'POST' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupMembersService.AddGroupMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupMembersServiceUrl({
        protoPath: '/v2/groups/{groupId}/members',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'members.joinedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __addGroupMembers;
}

/**
 * Removes site members from a specific group.
 *
 * Only group admins can remove members from their group.
 */
export function removeGroupMembers(
  payload: object
): RequestOptionsFactory<any> {
  function __removeGroupMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_member',
      method: 'DELETE' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupMembersService.RemoveGroupMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupMembersServiceUrl({
        protoPath: '/v2/groups/{groupId}/members',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __removeGroupMembers;
}

/**
 * Retrieves a list of up to 100 group members, given the provided paging.
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 *
 * Default sorts by `joinedDate` in descending order.
 */
export function listGroupMembers(payload: object): RequestOptionsFactory<any> {
  function __listGroupMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_member',
      method: 'GET' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupMembersService.ListGroupMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupMembersServiceUrl({
        protoPath: '/v2/groups/{groupId}/members',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'members.joinedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __listGroupMembers;
}

/**
 * Retrieves a list of up to 100 group members, given the provided paging, sorting and filtering.
 *
 *
 * Creates a query to retrieve a list of group members.
 *
 * > **Note:** For `SECRET` groups, only group admins and group members can query group members. <!-- However, if the `suppressAuth` option is set to `true`, all permissions are overwritten, and all site members (including non-group members) can query group members.  -->
 *
 * The `queryGroupMembers()` function builds a query to retrieve a list of all group members, and returns a [GroupMembersQueryBuilder](#membersquerybuilder) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](#membersquerybuilder/find) function.
 *
 * You can refine the query by chaining `GroupMembersQueryBuilder` functions onto the query. `GroupMembersQueryBuilder` functions enable you to sort, filter, and control the results that `queryMembers()` returns.
 *
 *  `queryGroupMembers()` runs with these `GroupMembersQueryBuilder` defaults, which you can override:
 *  + [`limit(100)`](/members-query-builder/limit)
 *  + [`descending("joinedDate")`](/members-query-builder/descending)
 *
 * The following `GroupMembersQueryBuilder` functions are supported for `queryGroupMembers()`. For a full description of the Members object, see the object returned for the [`items`](/members-query-result/items) property in `MembersQueryResult`.
 */
export function queryGroupMembers(payload: object): RequestOptionsFactory<any> {
  function __queryGroupMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_member',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupMembersService.QueryGroupMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupMembersServiceUrl({
        protoPath: '/v2/groups/{groupId}/members/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'members.joinedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __queryGroupMembers;
}

/**
 * Retrieves a list of group memberships of a site member.
 *
 * Only Wix users can retrieve a site member's group memberships.
 * Site members can only retrieve a list of their own group memberships.
 *
 * Default sorts by `groupId` in descending order.
 */
export function listMemberships(payload: object): RequestOptionsFactory<any> {
  function __listMemberships({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_member',
      method: 'GET' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupMembersService.ListMemberships',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupMembersServiceUrl({
        protoPath: '/v2/members/{memberId}/memberships',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listMemberships;
}

/**
 * Retrieves a list of up to 100 members and their membership status, given the provided paging and filtering.
 *
 * The queryMemberships function returns a Promise that resolves to a list of memberships.
 *
 * >**Note:** Site members can only query their own memberships.
 *
 * | Property                    | Supported Filters & Sorting                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                            |
 * | --------------------------- | -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
 * | `status`                    | [`eq()`](#membershipsquerybuilder/eq), [`ne()`](#membershipsquerybuilder/ne)             |
 * | `role`                      | [`eq()`](#membershipsquerybuilder/eq), [`ne()`](#membershipsquerybuilder/ne)             |
 *
 */
export function queryMemberships(payload: object): RequestOptionsFactory<any> {
  function __queryMemberships({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_member',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupMembersService.QueryMemberships',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupMembersServiceUrl({
        protoPath: '/v2/members/{memberId}/memberships/query',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __queryMemberships;
}
